import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  ComponentRef,
  Injector,
  ViewContainerRef,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import {
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { Subject, take, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { LineChartComponent } from '../../../../../shared/charts/components/linechart/linechart.component';
import { CommonService } from '../../../../../shared/services/common.service';
import { OrgScheduleList } from '../../../../manage-settings/manage-settings.interface';
import {
  BehaviorGraphDataResponse,
  EnterDataDrawerParams,
  GraphFilterEvent,
} from '../insights-data.interface';
import { InsightsDataService } from '../insights-data.service';
import { addDays, format } from 'date-fns';
import { PartialIntervalGraphComponent } from './partial-interval-graph/partial-interval-graph.component';
import { CommonModule } from '@angular/common';
import { LatencyGraphComponent } from './latency-graph/latency-graph.component';
import { FrequencyDatasheetGraphComponent } from './frequency-datasheet-graph/frequency-datasheet-graph.component';
import { ReplacementBehaviorGraphComponent } from './replacement-behavior-graph/replacement-behavior-graph.component';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { startOfWeek } from 'date-fns';
import { parse } from 'date-fns';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NoDataComponent } from '../../../../../pages/content/no-data/no-data.component';
import { FrequencyIntervalGraphComponent } from './frequency-interval-graph/frequency-interval-graph.component';
import { TreatmentPhasesComponent } from '../treatment-phases/treatment-phases.component';
import { startOfDay } from 'date-fns';
import { isAfter } from 'date-fns';
import { getDay } from 'date-fns';
import { PrintGraphComponent } from './print-graph/print-graph.component';
import { CustomTimeService } from '../../../../manage-settings/custom-time/custom-time.service';
import { OrganizationModel } from '../../../../organizations/organization.interface';
import { OrganizationService } from '../../../../organizations/organization.service';
import { ManageGraphComponent } from '../manage-graph.component';
import { GraphFilterComponent } from './graph-filter/graph-filter.component';
import { DATE_FORMAT } from '../../feedback/feeback.enum';

@Component({
  selector: 'app-data-graphs',
  standalone: true,
  imports: [
    NzDatePickerModule,
    NzInputModule,
    NzButtonModule,
    NzFormModule,
    NzFormLabelComponent,
    NzFormItemComponent,
    NzSelectModule,
    SvgIconComponent,
    LineChartComponent,
    PartialIntervalGraphComponent,
    FrequencyDatasheetGraphComponent,
    LatencyGraphComponent,
    ReplacementBehaviorGraphComponent,
    FrequencyIntervalGraphComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzSpinModule,
    NoDataComponent,
    GraphFilterComponent,
  ],
  templateUrl: './data-graph.component.html',
  styleUrl: './data-graph.component.scss',
})
export class DataGraphComponent
  extends ManageGraphComponent
  implements OnInit, OnDestroy
{
  private readonly destroy$ = new Subject<void>();
  drawerParams: EnterDataDrawerParams = {} as EnterDataDrawerParams;
  scheduleData: OrgScheduleList = {} as OrgScheduleList;
  form!: FormGroup;
  dateFormat = 'MM/dd/yyyy';
  placement = 'top';
  datasheetData: { id: number; value: string }[] = [
    { id: 1, value: 'Frequency/Duration Based Datasheet Graph' },
    { id: 2, value: 'Latency' },
    { id: 3, value: 'Partial Interval' },
    { id: 4, value: 'Replacement Behavior' },
  ];
  selectedUnit = 1;
  selectedGraph: number | null = null;
  startOfWeekMonday = startOfWeek(new Date(), { weekStartsOn: 1 });
  // Get the current week's Friday (4 days after Monday)
  endOfWeekFriday = addDays(this.startOfWeekMonday, 4);
  isLoading = false;
  isPrintLoading = false;
  responseData: BehaviorGraphDataResponse | null = null;
  disableFutureAndWeekends = (current: Date): boolean => {
    const today = startOfDay(new Date()); // Get today without time

    return (
      isAfter(current, today) || // Disable future dates
      getDay(current) === 0 || // Disable Sundays
      getDay(current) === 6 // Disable Saturdays
    );
  };
  constructor(
    private drawerService: DrawerService,
    private commonService: CommonService,
    private dataService: InsightsDataService,
    cd: ChangeDetectorRef,
    private fb: FormBuilder,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private orgService: OrganizationService,
    private customTimeService: CustomTimeService
  ) {
    super(cd);
    this.form = this.fb.group({
      datasheet: [null, [Validators.required]],
      graph: [{ value: null, disable: true }],
      dateRange: [
        [this.startOfWeekMonday, this.endOfWeekFriday],
        [Validators.required],
      ],
      graphImage: [''],
      graphImages: [[]],
      graphImageTotal: [''],
      graphImagesTotal: [[]],
      viewType: [1, Validators.required],
      isTrendLineEnabled: [false],
    });
    this.setupDrawerSubscription();
  }

  ngOnInit(): void {
    this.form
      .get('datasheet')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res === 1) {
          this.form.get('graph')?.enable();
          this.form.get('graph')?.setValue(1);
        } else {
          this.form.get('graph')?.setValue(null);
          this.form.get('graph')?.disable();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<EnterDataDrawerParams>(DRAWER_IDS.DATA_GRAPH)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParams = params;
          this.form.get('datasheet')?.setValue(this.drawerParams.selectedUnit);
          this.form.get('datasheet')?.setValue(this.drawerParams.selectedUnit);
          if (this.drawerParams.selectedUnit === 1) {
            this.form.get('graph')?.setValue(1);
          } else {
            this.form.get('graph')?.disable();
          }
          this.getBehvaiorGraphData();
        }
      });
  }

  getTitle() {
    return (
      this.datasheetData.find(d => d.id === this.selectedUnit)?.value ?? ''
    );
  }

  onFilterChange(event: GraphFilterEvent) {
    this.form.patchValue({
      viewType: event.viewType,
      isTrendLineEnabled: event.isTrendLineEnabled,
      dateRange: [
        parse(event.startDate, DATE_FORMAT, new Date()),
        parse(event.endDate, DATE_FORMAT, new Date()),
      ],
    });
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  onChange(event: boolean, weekDays: any): void {
    if (!event && weekDays.inputValue) {
      const date = parse(weekDays.inputValue, this.dateFormat, new Date());

      const monday = startOfWeek(date, { weekStartsOn: 1 }); // Get Monday
      const friday = addDays(monday, 4);
      this.form.get('dateRange')?.setValue([monday, friday]);
    }
  }

  changeFilter() {
    this.form.patchValue({
      graphImage: '',
      graphImageTotal: '',
      graphImages: [],
      graphImagesTotal: [],
    });
    this.getBehvaiorGraphData();
  }

  value(controlName: string) {
    return this.form.get(controlName)?.value;
  }
  getBehvaiorGraphData() {
    this.isGraphDataAvailable = false;
    this.isLoading = true;
    this.selectedUnit = this.value('datasheet');
    this.selectedGraph =
      this.selectedUnit === 1 ? this.form.get('graph')?.value : null;
    this.dataService
      .getBehaviorGraphData({
        CaseBehaviorId: this.drawerParams.caseBehaviorId,
        UnitName: this.form.get('datasheet')?.value,
        StartDate: format(this.form.get('dateRange')?.value[0], DATE_FORMAT),
        EndDate: format(this.form.get('dateRange')?.value[1], DATE_FORMAT),
        graphType: this.selectedGraph || undefined,
        viewType: this.form.get('viewType')?.value || 1,
      })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          if (res.result && res.result) {
            this.responseData = res.result;
            this.manageBehvaiorGraphResponseData(res.result);
          } else {
            this.isGraphDataAvailable = false;
            this.displayNoData = true;
            this.replacementGraphData = null;
            this.frequencyDatasheetGraphData = null;
            this.latencyGraphData = null;
            this.partialIntervalGraphData = null;
          }
          this.isLoading = false;
          this.cd.detectChanges();
        },
        error: (error: ErrorResponse) => {
          this.isLoading = false;
          this.commonService.notify('error', error.message);
        },
      });
  }

  async manageBehvaiorGraphResponseData(data: BehaviorGraphDataResponse) {
    const selectedUnit = this.form.get('datasheet')?.value;
    const graphType = this.form.get('graph')?.value;
    const viewType = this.form.get('viewType')?.value || 1;
    const isTrendLineEnabled = this.form.get('isTrendLineEnabled')?.value;

    const success = await this.processBehaviorGraphData(
      data,
      selectedUnit,
      graphType,
      viewType,
      isTrendLineEnabled
    );

    if (!success) {
      this.isGraphDataAvailable = false;
      this.displayNoData = true;
    }
  }

  openTreatmentPhasesDrawer(): void {
    const treatmentPhaseDrawerRef = this.drawerService.openDrawer(
      TreatmentPhasesComponent,
      'Data',
      1170,
      true,
      undefined,
      this.responseData?.id,
      DRAWER_IDS.DATA_GRAPH_TREATMENT_PHASE
    );
    treatmentPhaseDrawerRef.afterClose
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getBehvaiorGraphData();
      });
  }

  onChartImageGenerated(image: string, type = '') {
    if (type === 'total') {
      this.form.patchValue({
        graphImageTotal: image,
      });
    } else {
      this.form.patchValue({
        graphImage: image,
      });
    }
  }

  onAllChartImagesRenderd(images: string[], type = '') {
    if (type === 'total') {
      this.form.patchValue({
        graphImagesTotal: images,
      });
    } else {
      this.form.patchValue({
        graphImages: images,
      });
    }
  }

  async printGraph() {
    const factory = this.viewContainerRef.createComponent(PrintGraphComponent, {
      injector: this.injector,
    });
    const componentElement = factory.location.nativeElement;
    componentElement.style.display = 'none';
    this.isPrintLoading = true;
    await Promise.all([
      this.orgService
        .getOrganization(this.drawerParams?.caseDetail?.student.organizationId)
        .toPromise(),
    ])
      .then(async ([orgRes]: [OrganizationModel | undefined]) => {
        let base64Image = '';
        if (orgRes && orgRes.logoUrl) {
          base64Image = await this.commonService.getBase64ImageFromUrl(
            orgRes.logoUrl
          );
        } else {
          base64Image = await this.commonService.getBase64ImageFromUrl(
            '/assets/images/logo.png'
          );
        }
        factory.instance.graphData = {
          logo: base64Image,
          orgDetail: orgRes,
          caseDetail: this.drawerParams.caseDetail,
          graphData: this.form.value,
          behavior: this.drawerParams.behavior,
          behaviorNames: this.drawerParams.behavior?.behaviorCategoryTypes.map(
            item => item.behaviorTypeName
          ),
        };
        this.waitForInitialization(factory).then(async () => {
          await factory.instance.printPDF();
          factory.instance.graphData = null;
          factory.destroy();
        });
      })
      .catch(e => console.error(e))
      .finally(() => {
        this.isPrintLoading = false;
        this.cd.detectChanges();
      });
  }
  waitForInitialization(factory: ComponentRef<PrintGraphComponent>) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }
}
